import { useEffect, useState } from "react";
import { Phone, Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import type { EmbeddedContact } from "@/api/types";

interface StartCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  contacts: EmbeddedContact[];
  /** Fires once the user picks a contact and confirms — consumer
   *  starts the timer + closes the dialog. */
  onStart: (contact: EmbeddedContact) => void;
}

export function StartCallDialog({
  open,
  onOpenChange,
  companyName,
  contacts,
  onStart,
}: StartCallDialogProps) {
  // Pre-pick the decision-maker when the company has one flagged;
  // the user can still override before confirming.
  const initial = (() => {
    const dm = contacts.find((c) => c.is_decision_maker);
    if (dm) return String(dm.id);
    if (contacts[0]) return String(contacts[0].id);
    return null;
  })();
  const [pickId, setPickId] = useState<string | null>(initial);

  // Reset on open so reopening doesn't carry a stale pick.
  useEffect(() => {
    if (open) setPickId(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function confirm() {
    if (!pickId) return;
    const picked = contacts.find((c) => String(c.id) === pickId);
    if (!picked) return;
    onStart(picked);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
            <Phone className="h-4 w-4 text-emerald-600" />
          </div>
          <DialogTitle>Start a call</DialogTitle>
          <DialogDescription>
            Track an outgoing call to {companyName}. The timer starts
            now and stops when you click Stop.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Contact
          </label>
          {contacts.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
              No contacts on this company yet, add one before starting
              a call so the log knows who you spoke to.
            </p>
          ) : (
            <Select
              value={pickId ?? ""}
              onValueChange={(v) => setPickId(v)}
            >
              <SelectTrigger className="h-10 rounded-lg border-slate-200 bg-white">
                <SelectValue placeholder="Pick a contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    <span className="flex items-center gap-2">
                      <span className="truncate">{c.full_name}</span>
                      {c.is_decision_maker && (
                        <span className="rounded-full bg-indigo-100 px-1.5 py-0 text-[9px] font-semibold text-indigo-700">
                          DM
                        </span>
                      )}
                      {c.title && (
                        <span className="truncate text-[11px] text-slate-400">
                          {c.title}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirm}
            disabled={!pickId || contacts.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Play className="mr-2 h-3.5 w-3.5 fill-current" />
            Start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
